/**
 * BatchActionBar — floating multi-select action bar for the Product Dev list.
 *
 * Appears fixed at bottom-centre whenever ≥1 row is selected. Card-like
 * (border + shadow), brand-purple primary discipline (DESIGN.md §1). It is a
 * selection action bar (ClickUp-style), NOT a page action bar — the sticky-top
 * convention (§3) does not apply; this is the standard home for bulk actions on
 * a multi-select table.
 *
 * Compact layout (design audit A2): the count is a single-line chip that never
 * wraps; the high-frequency actions (priority, status, assign, archive, clear)
 * stay top-level; the three low-frequency actions (set brand, set capsule, set
 * expected date) collapse into a "More" menu so the bar fits on any viewport.
 * The action cluster lives in an `overflow-x-auto` scroller as the last-resort
 * degradation path. The bar caps at `calc(100vw-2rem)` so it can never bleed
 * past the viewport edge.
 *
 * Each control fires ONE `batchUpdate` call, then invalidates the list +
 * clears the selection (via `onDone`). Status changes route through a
 * lightweight AlertDialog confirm first (they are the highest-blast-radius
 * field). Success/error surfaces as a toast.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { add, format } from 'date-fns';

import {
  Archive,
  CalendarDays,
  Check,
  Flag,
  Layers,
  Loader2,
  MessageSquareText,
  MoreHorizontal,
  Pencil,
  ShoppingCart,
  Tag,
  UserPlus,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { FloatingSelectionBar } from '@/components/layout/FloatingSelectionBar';
import { cn } from '@/lib/utils';
import { useOrganization } from '@/contexts/OrganizationContext';
import {
  useProductDevBatchMentionableUsers,
  useProductDevMutations,
} from '@/features/product-dev/hooks/useProductDev';
import {
  PRODUCT_DEV_STATUSES,
  PRODUCT_DEV_PRIORITIES,
  PRIORITY_ICON_CLASS,
  STATUS_PILL_CLASS,
} from '@/features/product-dev/utils/status-style';
import {
  BatchUpdateSheet,
  type ProductDevBatchApply,
  type ProductDevBatchUpdates,
} from './BatchUpdateSheet';
import { SelectionOrderDialog, type SelectionOrderRecord } from './SelectionOrderDialog';

export function BatchActionBar({
  selectedIds,
  selectedProducts,
  members,
  onDone,
  editorOpen: controlledEditorOpen,
  onEditorOpenChange,
}: {
  selectedIds: string[];
  selectedProducts: SelectionOrderRecord[];
  members: Array<{ id: string; label: string }>;
  /** Clear the selection after a successful mutation. */
  onDone: () => void;
  /** Controlled by the list when a group header launches the review sheet. */
  editorOpen?: boolean;
  onEditorOpenChange?: (open: boolean) => void;
}) {
  const { t } = useTranslation('productDev');
  const { batchUpdate, batchComment, batchUploadFile } = useProductDevMutations();
  const { organization } = useOrganization();
  const count = selectedIds.length;

  // Org-defined brands power the Set-brand menu. Cast-gated: the parallel
  // backend change that adds `brands` to the settings schema may lag this file
  // (falls back to an empty list, so the "Custom brand" escape still works).
  const orgBrands = (
    (organization?.settings as { brands?: unknown } | null | undefined)?.brands ?? []
  );
  const brandOptions = Array.isArray(orgBrands)
    ? orgBrands.filter((b): b is string => typeof b === 'string' && b.trim() !== '')
    : [];

  const [dateOpen, setDateOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [brandOpen, setBrandOpen] = useState(false);
  const [brandValue, setBrandValue] = useState('');
  const [capsuleOpen, setCapsuleOpen] = useState(false);
  const [capsuleValue, setCapsuleValue] = useState('');
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [internalEditorOpen, setInternalEditorOpen] = useState(false);
  const editorOpen = controlledEditorOpen ?? internalEditorOpen;
  const mentionablePeopleQuery = useProductDevBatchMentionableUsers(
    selectedIds,
    editorOpen,
  );
  const mentionablePeople = mentionablePeopleQuery.data
    ? mentionablePeopleQuery.data.map((person) => ({
        id: person.id,
        label: person.display_name,
        organizationName: person.organization_name,
      }))
    : members.map((member) => ({ ...member, organizationName: null }));
  const setEditorOpen = (open: boolean) => {
    setInternalEditorOpen(open);
    onEditorOpenChange?.(open);
  };

  const run = async (updates: ProductDevBatchUpdates) => {
    try {
      const res = await batchUpdate.mutateAsync({ ids: selectedIds, updates });
      toast({ title: t('list.batch.toast.updated', { count: res.updated }) });
      onDone();
    } catch (err) {
      toast({
        title: t('list.batch.toast.failedTitle'),
        description: err instanceof Error ? err.message : t('list.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  const applyCoordinatedUpdate = async ({
    updates,
    note,
    mentions,
    attachment,
  }: ProductDevBatchApply) => {
    const hasUpdates = Object.keys(updates).length > 0;
    const completed: string[] = [];
    try {
      if (hasUpdates) {
        const result = await batchUpdate.mutateAsync({ ids: selectedIds, updates });
        if (result.updated !== count) {
          toast({
            title: `Updated ${result.updated} of ${count} products`,
            description: 'Some selected products changed or are no longer available. Refresh the list before adding the note.',
            variant: 'destructive',
          });
          return;
        }
        completed.push(t('list.batch.toast.savedFields'));
      }

      if (note) {
        const result = await batchComment.mutateAsync({
          entityIds: selectedIds,
          body: note,
          mentions: mentions.length > 0 ? mentions : undefined,
        });
        if (result.commented !== count || result.skipped > 0) {
          toast({
            title: `Added the note to ${result.commented} of ${count} products`,
            description: 'Some selected products changed or are no longer available. Refresh and review the remaining selection.',
            variant: 'destructive',
          });
          // The successful subset already has its timeline note. Close and
          // clear the stale selection so retrying cannot duplicate those notes.
          setEditorOpen(false);
          onDone();
          return;
        }
        completed.push(t('list.batch.toast.savedNote'));
      }

      if (attachment) {
        const result = await batchUploadFile.mutateAsync({
          entityIds: selectedIds,
          ...attachment,
        });
        if (result.uploaded !== count || result.skipped > 0 || result.failed > 0) {
          toast({
            title: t('list.batch.toast.filePartial', {
              uploaded: result.uploaded,
              count,
            }),
            description: t('list.batch.toast.filePartialBody'),
            variant: 'destructive',
          });
          // Prevent a blind retry from duplicating the successful independent
          // file copies on records that already received one.
          setEditorOpen(false);
          onDone();
          return;
        }
        completed.push(t('list.batch.toast.savedFile'));
      }

      toast({
        title: attachment && note && hasUpdates
          ? t('list.batch.toast.updatedFileNote', { count })
          : attachment && note
            ? t('list.batch.toast.fileNote', { count })
            : attachment && hasUpdates
              ? t('list.batch.toast.updatedFile', { count })
              : attachment
                ? t('list.batch.toast.fileOnly', { count })
                : note && hasUpdates
                  ? `Updated ${count} products and added the note`
                  : note
                    ? `Added the note to ${count} products`
                    : `Updated ${count} products`,
        description: mentions.length > 0
          ? t('list.batch.toast.scopedRecipients', { count: mentions.length })
          : undefined,
      });
      setEditorOpen(false);
      onDone();
    } catch (err) {
      toast({
        title: completed.length > 0
          ? t('list.batch.toast.someSaved')
          : 'Batch update failed',
        description: completed.length > 0
          ? t('list.batch.toast.someSavedBody', {
              saved: completed.join(', '),
              error: err instanceof Error ? err.message : t('list.toast.tryAgain'),
            })
          : err instanceof Error
            ? err.message
            : t('list.toast.tryAgain'),
        variant: 'destructive',
      });
      if (completed.length > 0) {
        // At least one non-idempotent action is already durable. Clear the
        // selection so a blind retry cannot duplicate notes or file copies.
        setEditorOpen(false);
        onDone();
      }
    }
  };

  const pending = batchUpdate.isPending || batchComment.isPending || batchUploadFile.isPending;
  const TRIGGER_CLASS = 'h-8 shrink-0 px-3 text-xs gap-1.5';
  return (
    <>
      {/* BLU-680: shared FloatingSelectionBar primitive — positioning, the
          polite live region, and enter/exit motion live there. The parent
          conditionally mounts this component, so `visible` is always true
          here (exit plays only for visibility-driven consumers). */}
      <FloatingSelectionBar
        visible
        srStatus={t('list.batch.selected', { count })}
        ariaLabel={t('list.batch.regionAria')}
        role="region"
        className="bottom-6 z-40"
        pillClassName="flex max-w-[calc(100vw-2rem)] items-center gap-2 rounded-lg border border-border bg-background/95 px-3 py-2 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/85"
      >
          {/* Count chip — single line, never wraps. */}
          <span className="shrink-0 whitespace-nowrap rounded-md bg-muted px-2 py-1 text-xs font-medium tabular-nums text-foreground">
            {t('list.batch.selected', { count })}
          </span>
          <span className="h-6 w-px shrink-0 bg-border" aria-hidden="true" />

          {/* Action cluster — horizontally scrollable as the degradation path
              when the viewport can't fit every control. The negative margin
              keeps focus rings from being clipped by the scroll overflow. */}
          <div className="-my-1 flex items-center gap-2 overflow-x-auto py-1">
            <Button
              size="sm"
              className={cn(TRIGGER_CLASS, 'bg-threadheads-purple text-white hover:bg-threadheads-purple/90')}
              onClick={() => setEditorOpen(true)}
              disabled={pending}
            >
              <MessageSquareText className="h-3.5 w-3.5" /> Update selected
            </Button>

            <Button
              variant="outline"
              size="sm"
              className={cn(TRIGGER_CLASS, 'border-threadheads-purple/40 text-threadheads-purple hover:text-threadheads-purple')}
              onClick={() => setOrderOpen(true)}
              disabled={pending}
            >
              <ShoppingCart className="h-3.5 w-3.5" /> {t('selectionOrder.action')}
            </Button>

            {/* Set priority */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className={TRIGGER_CLASS} disabled={pending}>
                  <Flag className="h-3.5 w-3.5" /> {t('list.batch.setPriority')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-44">
                {PRODUCT_DEV_PRIORITIES.map((p) => (
                  <DropdownMenuItem
                    key={p}
                    className="gap-2"
                    onClick={() => void run({ priority: p })}
                  >
                    <Flag className={cn('h-3.5 w-3.5', PRIORITY_ICON_CLASS[p])} />
                    {t(`priority.${p}`)}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem className="gap-2" onClick={() => void run({ priority: null })}>
                  <Flag className="h-3.5 w-3.5 text-muted-foreground/50" />
                  {t('priority.none')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Set status — routes through a confirm */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className={TRIGGER_CLASS} disabled={pending}>
                  {t('list.batch.setStatus')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-52">
                {PRODUCT_DEV_STATUSES.map((s) => (
                  <DropdownMenuItem key={s} className="gap-2" onClick={() => setPendingStatus(s)}>
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                        STATUS_PILL_CLASS[s] ?? 'bg-muted text-muted-foreground',
                      )}
                    >
                      {t(`status.${s}`)}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Assign to */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className={TRIGGER_CLASS} disabled={pending}>
                  <UserPlus className="h-3.5 w-3.5" /> {t('list.batch.assignTo')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="max-h-72 w-56 overflow-y-auto">
                {members.map((m) => (
                  <DropdownMenuItem key={m.id} onClick={() => void run({ assigned_to: m.id })}>
                    {m.label}
                  </DropdownMenuItem>
                ))}
                {members.length > 0 && <DropdownMenuSeparator />}
                <DropdownMenuItem
                  className="text-muted-foreground"
                  onClick={() => void run({ assigned_to: null })}
                >
                  {t('list.batch.unassign')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* More — the low-frequency actions live here so the bar stays
                compact everywhere. Menu items open the same brand/capsule/date
                surfaces as before. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className={TRIGGER_CLASS} disabled={pending}>
                  <MoreHorizontal className="h-3.5 w-3.5" /> {t('list.batch.more')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-52">
                {/* Set brand — org brands as a dropdown, plus a free-text escape. */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="gap-2">
                    <Tag className="h-3.5 w-3.5" /> {t('list.batch.setBrand')}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent className="max-h-72 w-56 overflow-y-auto">
                      {brandOptions.map((b) => (
                        <DropdownMenuItem key={b} onClick={() => void run({ brand: b })}>
                          {b}
                        </DropdownMenuItem>
                      ))}
                      {brandOptions.length > 0 && <DropdownMenuSeparator />}
                      <DropdownMenuItem className="gap-2" onSelect={() => setBrandOpen(true)}>
                        <Pencil className="h-3.5 w-3.5" /> {t('list.batch.brandCustom')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-muted-foreground"
                        onClick={() => void run({ brand: null })}
                      >
                        {t('list.batch.clearBrand')}
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>

                {/* Set capsule — free text (no curated list). */}
                <DropdownMenuItem className="gap-2" onSelect={() => setCapsuleOpen(true)}>
                  <Layers className="h-3.5 w-3.5" /> {t('list.batch.setCapsule')}
                </DropdownMenuItem>

                {/* Set expected date */}
                <DropdownMenuItem className="gap-2" onSelect={() => setDateOpen(true)}>
                  <CalendarDays className="h-3.5 w-3.5" /> {t('list.batch.setDate')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <span className="h-6 w-px shrink-0 bg-border" aria-hidden="true" />

          {/* Archive — destructive-styled, confirm first. */}
          <Button
            variant="outline"
            size="sm"
            className="h-8 shrink-0 gap-1.5 border-destructive/40 px-3 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => setArchiveOpen(true)}
            disabled={pending}
          >
            <Archive className="h-3.5 w-3.5" /> {t('list.batch.archive')}
          </Button>

          <span className="h-6 w-px shrink-0 bg-border" aria-hidden="true" />
          <Button
            variant="ghost"
            size="sm"
            className="h-8 shrink-0 px-2 text-xs"
            onClick={onDone}
            disabled={pending}
          >
            {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('list.batch.clear')}
          </Button>
      </FloatingSelectionBar>

      <BatchUpdateSheet
        open={editorOpen}
        onOpenChange={setEditorOpen}
        selectedProducts={selectedProducts}
        members={members}
        mentionablePeople={mentionablePeople}
        mentionablePeopleLoading={mentionablePeopleQuery.isLoading}
        pending={pending}
        onApply={applyCoordinatedUpdate}
      />

      <SelectionOrderDialog
        open={orderOpen}
        onOpenChange={setOrderOpen}
        records={selectedProducts}
        onComplete={onDone}
      />

      {/* Set brand — custom free-text. Opened from the More menu. */}
      <Dialog
        open={brandOpen}
        onOpenChange={(o) => {
          if (pending) return;
          setBrandOpen(o);
          if (!o) setBrandValue('');
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('list.batch.setBrand')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const v = brandValue.trim();
              if (!v) return;
              setBrandOpen(false);
              setBrandValue('');
              void run({ brand: v });
            }}
          >
            <Input
              autoFocus
              value={brandValue}
              onChange={(e) => setBrandValue(e.target.value)}
              placeholder={t('list.batch.brandPlaceholder')}
              maxLength={120}
              className="h-9"
              aria-label={t('list.batch.setBrand')}
            />
            <Button
              type="submit"
              className="h-9 shrink-0 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
              disabled={!brandValue.trim()}
            >
              {t('list.batch.apply')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Set capsule — free text. Opened from the More menu. */}
      <Dialog
        open={capsuleOpen}
        onOpenChange={(o) => {
          if (pending) return;
          setCapsuleOpen(o);
          if (!o) setCapsuleValue('');
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('list.batch.setCapsule')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const v = capsuleValue.trim();
              if (!v) return;
              setCapsuleOpen(false);
              setCapsuleValue('');
              void run({ capsule: v });
            }}
          >
            <Input
              autoFocus
              value={capsuleValue}
              onChange={(e) => setCapsuleValue(e.target.value)}
              placeholder={t('list.batch.capsulePlaceholder')}
              maxLength={120}
              className="h-9"
              aria-label={t('list.batch.setCapsule')}
            />
            <Button
              type="submit"
              className="h-9 shrink-0 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
              disabled={!capsuleValue.trim()}
            >
              {t('list.batch.apply')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Set expected date — quick presets + calendar + clear. Opened from More. */}
      <Dialog open={dateOpen} onOpenChange={(o) => !pending && setDateOpen(o)}>
        {/* dialog-overflow-ok: content-sized dialog (calendar) keeps an auto track so it sizes to the calendar */}
        <DialogContent className="w-auto max-w-fit grid-cols-[auto] p-0">
          <DialogHeader className="px-4 pt-4">
            <DialogTitle>{t('list.batch.setDate')}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap gap-1.5 border-b p-2">
            {[
              { key: 'deadline.quickWeek', duration: { weeks: 1 } },
              { key: 'deadline.quickTwoWeeks', duration: { weeks: 2 } },
              { key: 'deadline.quickMonth', duration: { months: 1 } },
            ].map((q) => (
              <Button
                key={q.key}
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => {
                  setDateOpen(false);
                  void run({
                    expected_completion_date: format(add(new Date(), q.duration), 'yyyy-MM-dd'),
                  });
                }}
              >
                {t(q.key)}
              </Button>
            ))}
          </div>
          <Calendar
            mode="single"
            onSelect={(date) => {
              if (!date) return;
              setDateOpen(false);
              void run({ expected_completion_date: format(date, 'yyyy-MM-dd') });
            }}
            initialFocus
            className="pointer-events-auto"
          />
          <div className="border-t p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-full px-2 text-xs text-muted-foreground hover:text-destructive"
              onClick={() => {
                setDateOpen(false);
                void run({ expected_completion_date: null });
              }}
            >
              <X className="mr-1.5 h-3.5 w-3.5" /> {t('list.batch.clearDate')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pendingStatus !== null}
        onOpenChange={(o) => !o && setPendingStatus(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('list.batch.confirm.statusTitle', {
                status: pendingStatus ? t(`status.${pendingStatus}`) : '',
                count,
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('list.batch.confirm.statusBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('list.dialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
              onClick={() => {
                const s = pendingStatus;
                setPendingStatus(null);
                if (s) void run({ status: s });
              }}
            >
              <Check className="mr-1.5 h-3.5 w-3.5" /> {t('list.batch.confirm.confirmCta')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('list.batch.confirm.archiveTitle', { count })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('list.batch.confirm.archiveBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('list.dialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                setArchiveOpen(false);
                void run({ archive: true });
              }}
            >
              <Archive className="mr-1.5 h-3.5 w-3.5" /> {t('list.batch.confirm.archiveCta')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
